<script setup lang="ts">
  import { computed, nextTick, ref } from "vue";
  import { Ellipsis } from "lucide-vue-next";
  import { Button, buttonVariants } from "@/components/ui/button";
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from "@/components/ui/dropdown-menu";
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from "@/components/ui/alert-dialog";
  import { cn } from "@/lib/utils";
  import type { ActionItem } from "./types";

  const props = withDefaults(
    defineProps<{
      actions: ActionItem[];
      token: string;
      disabled?: boolean;
      deleteTitle?: string;
      deleteDescription?: string | null;
      deleteCancelLabel?: string;
      deleteConfirmLabel?: string;
    }>(),
    {
      disabled: false,
      deleteTitle: "Do you want to proceed?",
      deleteDescription: null,
      deleteCancelLabel: "No, stay here",
      deleteConfirmLabel: "Yes, delete",
    },
  );

  const dropdownOpen = ref(false);
  const deleteDialogOpen = ref(false);
  const deleteLabel = ref("this item");
  const deleteHandler = ref<(() => void) | null>(null);

  const closeDeleteDialog = () => {
    deleteDialogOpen.value = false;
    deleteLabel.value = "this item";
    deleteHandler.value = null;
  };

  const openDeleteDialog = async (action: ActionItem) => {
    dropdownOpen.value = false;
    deleteLabel.value = action.msgConfirm ?? "this item";
    deleteHandler.value = () => action.function(props.token);
    await nextTick();
    deleteDialogOpen.value = true;
  };

  const confirmDelete = () => {
    const handler = deleteHandler.value;
    closeDeleteDialog();
    handler?.();
  };

  const handleDeleteDialogOpenChange = (isOpen: boolean) => {
    deleteDialogOpen.value = isOpen;
  };

  const handleActionSelect = (action: ActionItem, event: Event) => {
    if (action.isDelete) {
      event.preventDefault();
      openDeleteDialog(action);
      return;
    }

    action.function(props.token);
  };

  const filteredActions = computed(() => {
    return props.actions.filter((action) => action.condition !== false);
  });
</script>

<template>
  <DropdownMenu v-model:open="dropdownOpen">
    <DropdownMenuTrigger as-child>
      <Button
        variant="ghost"
        size="icon-sm"
        :disabled="disabled"
        @click.stop.prevent>
        <Ellipsis class="h-4 w-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem
        v-for="action in filteredActions"
        :key="`action-${action.text}`"
        class="capitalize"
        @select="handleActionSelect(action, $event)">
        <component
          :is="action.icon"
          class="h-4 w-4" />
        {{ action.text }}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>

  <AlertDialog
    :open="deleteDialogOpen"
    @update:open="handleDeleteDialogOpenChange">
    <AlertDialogContent class="font-sans">
      <AlertDialogHeader>
        <AlertDialogTitle>{{ deleteTitle }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ deleteDescription || `Are you sure you want to delete ${deleteLabel}? This change cannot be undone.` }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel as-child>
          <button
            type="button"
            :class="cn(buttonVariants({ variant: 'outline' }), 'mt-2 sm:mt-0')"
            @click="closeDeleteDialog">
            {{ deleteCancelLabel }}
          </button>
        </AlertDialogCancel>
        <AlertDialogAction as-child>
          <button
            type="button"
            :class="buttonVariants({ variant: 'destructive' })"
            @click="confirmDelete">
            {{ deleteConfirmLabel }}
          </button>
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
